import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Bell, BellOff, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * MatchAlertToggle — one-click "Notify me when any of my subscribed teams
 * goes on court". Uses the browser Notification API + public SSE stream.
 * teamIds prop (from parent) is merged with the user's /me/favourites list,
 * so an owner sees their own team AND any teams they've favourited elsewhere.
 * Re-fetches favourites when a 'mara:favourites-changed' window event fires.
 */
const STORAGE_KEY = "mara_match_alerts_enabled";

export default function MatchAlertToggle({ teamId, teamName, extraTeamIds = [] }) {
  const [enabled, setEnabled] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
  });
  const [perm, setPerm] = useState(() => (typeof Notification !== "undefined" ? Notification.permission : "unsupported"));
  const [favIds, setFavIds] = useState([]);
  const [favNames, setFavNames] = useState({});
  const esRef = useRef(null);
  const recentIdsRef = useRef(new Set());

  const loadFavs = async () => {
    try {
      const { data } = await api.get("/me/favourites");
      setFavIds(data?.team_ids || []);
      const names = {};
      (data?.teams || []).forEach((t) => { names[t.id] = t.name; });
      setFavNames(names);
    } catch { /* not signed in — silent */ }
  };
  useEffect(() => { loadFavs(); }, []);
  useEffect(() => {
    const h = () => loadFavs();
    window.addEventListener("mara:favourites-changed", h);
    return () => window.removeEventListener("mara:favourites-changed", h);
  }, []);

  const subscribedIds = React.useMemo(() => {
    const s = new Set([...(teamId ? [teamId] : []), ...(extraTeamIds || []), ...favIds]);
    return [...s].filter(Boolean);
  }, [teamId, extraTeamIds, favIds]);

  const nameOf = (id) => favNames[id] || (id === teamId ? teamName : null) || "Your team";

  const notify = (title, body) => {
    try {
      if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        new Notification(title, { body, icon: "/mara-logo.png", tag: `mara-${Date.now()}` });
      }
    } catch { /* ignore */ }
    toast(title, { description: body });
  };

  useEffect(() => {
    if (!enabled || subscribedIds.length === 0) {
      if (esRef.current) { esRef.current.close(); esRef.current = null; }
      return;
    }
    const base = (api.defaults.baseURL || "").replace(/\/api\/?$/, "");
    const es = new EventSource(`${base}/api/public/events/stream`);
    esRef.current = es;

    const involvedTeam = (m) => {
      if (subscribedIds.includes(m.team_a_id)) return { id: m.team_a_id, opp: m.team_b };
      if (subscribedIds.includes(m.team_b_id)) return { id: m.team_b_id, opp: m.team_a };
      return null;
    };

    const handleStarted = async (evt) => {
      try {
        const data = JSON.parse(evt.data || "{}");
        const matchId = data.match_id || data.id;
        if (!matchId || recentIdsRef.current.has(`s-${matchId}`)) return;
        const { data: m } = await api.get(`/public/matches/${matchId}`);
        const inv = involvedTeam(m);
        if (inv) {
          recentIdsRef.current.add(`s-${matchId}`);
          setTimeout(() => recentIdsRef.current.delete(`s-${matchId}`), 60 * 1000);
          notify(`${nameOf(inv.id)} is on court`, `vs ${inv.opp?.name || "opponent"} · ${m.court || "TBD"}${m.seed ? ` · Seed ${m.seed}` : ""}`);
        }
      } catch { /* ignore */ }
    };
    const handleFinal = async (evt) => {
      try {
        const data = JSON.parse(evt.data || "{}");
        const matchId = data.match_id || data.id;
        if (!matchId || recentIdsRef.current.has(`f-${matchId}`)) return;
        const { data: m } = await api.get(`/public/matches/${matchId}`);
        const inv = involvedTeam(m);
        if (inv) {
          recentIdsRef.current.add(`f-${matchId}`);
          setTimeout(() => recentIdsRef.current.delete(`f-${matchId}`), 60 * 1000);
          const label = m.winner_team_id === inv.id ? "WIN" : (m.winner_team_id ? "LOSS" : "FINAL");
          const gA = m.total_games_won_a ?? m.score_a ?? 0;
          const gB = m.total_games_won_b ?? m.score_b ?? 0;
          notify(`${label}: ${nameOf(inv.id)} vs ${inv.opp?.name || "opponent"}`, `Final ${gA}–${gB}${m.seed ? ` · Seed ${m.seed}` : ""}`);
        }
      } catch { /* ignore */ }
    };

    es.addEventListener("match.started", handleStarted);
    es.addEventListener("match.finalized", handleFinal);
    es.onerror = () => { /* auto-reconnect */ };
    return () => es.close();
  }, [enabled, subscribedIds]);

  const toggle = async () => {
    if (typeof Notification === "undefined") {
      toast.error("Your browser doesn't support desktop notifications");
      return;
    }
    if (enabled) {
      setEnabled(false);
      try { localStorage.setItem(STORAGE_KEY, "0"); } catch {}
      toast.success("Match alerts turned off");
      return;
    }
    let permission = Notification.permission;
    if (permission === "default") {
      permission = await Notification.requestPermission();
    }
    setPerm(permission);
    if (permission !== "granted") {
      toast.error("Enable notifications in your browser to get match alerts");
      return;
    }
    setEnabled(true);
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch {}
    const count = subscribedIds.length;
    toast.success(count > 1 ? `Alerts on for ${count} teams` : "You'll be notified when your team goes on court");
  };

  const unsupported = typeof Notification === "undefined";
  const count = subscribedIds.length;
  return (
    <Button
      onClick={toggle}
      disabled={unsupported}
      variant={enabled ? "default" : "outline"}
      className={enabled ? "bg-emerald-500 text-black hover:bg-emerald-400 font-bold uppercase tracking-widest text-xs" : "text-xs uppercase tracking-widest"}
      data-testid="match-alert-toggle"
      title={unsupported ? "Notifications unsupported" : (enabled ? `Alerts ON for ${count} team${count !== 1 ? "s" : ""} — click to disable` : "Enable match alerts")}
    >
      {enabled ? <CheckCircle2 className="w-4 h-4 mr-1.5" /> : perm === "denied" ? <BellOff className="w-4 h-4 mr-1.5" /> : <Bell className="w-4 h-4 mr-1.5" />}
      {enabled ? `Alerts ON${count > 1 ? ` · ${count}` : ""}` : "Alert me"}
    </Button>
  );
}
